// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_ACCOUNTS } from '../../../state_manager/globals.ts'
import { recordHasTotalLabel, stripTotalTextFromCellNode } from '../../../core/utils/recordRowBold.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'

interface ScheduleOfInvestmentsTableProps {
  data: Record<string, unknown>[]
  loading: boolean
  gridColumns: ViewConfigColumn[]
}

export const ScheduleOfInvestmentsTable = React.memo(function ScheduleOfInvestmentsTable({
  data,
  loading,
  gridColumns
}: ScheduleOfInvestmentsTableProps) {
  const { openFormInTab } = useTabManagerContext()
  const soiConfigs = useMemo(() => gridColumns, [gridColumns])
  const openRecordFullColumns = useMemo(
    () => new Set(soiConfigs.filter((c) => c.fieldKey === 'open-record-full').map((c) => c.valueKey)),
    [soiConfigs]
  )

  const { columns: baseColumns, frozenColumns } = useMemo(() => {
    return createTableColumns({
      columns: soiConfigs,
      fallbackRecordModuleKey: 'companies'
    })
  }, [soiConfigs])

  const firstColumnValueKey = soiConfigs[0]?.valueKey

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const prevRender = col.render
      if (!prevRender) return col
      const isFirstColumn = firstColumnValueKey != null && col.key === firstColumnValueKey
      const opensRecordFull = openRecordFullColumns.has(String(col.key))
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)
          const isTotalRow = recordHasTotalLabel(record)
          const renderedNode = isTotalRow && !isFirstColumn ? stripTotalTextFromCellNode(node) : node

          if (!opensRecordFull || isTotalRow) return renderedNode

          const accountId = typeof record.accounts__id === 'string' ? record.accounts__id : ''
          if (!accountId) return renderedNode

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab(TABLE_ACCOUNTS, accountId, String(record.accounts__name ?? ''))}
            >
              {renderedNode}
            </button>
          )
        }
      }
    })
  }, [baseColumns, firstColumnValueKey, openRecordFullColumns, openFormInTab])

  const dataSource = useMemo(() => {
    const src = data ?? []
    return src.map((row, i) => {
      const id = [
        row.accounts__name ?? '',
        row.funds__name ?? '',
        row.vintage__label ?? '',
        row.tranches__name ?? '',
        i
      ].join('-')
      return {
        ...row,
        id,
        key: id
      }
    })
  }, [data])

  if (columns.length === 0) return null

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        frozenColumns={frozenColumns}
        rowClassName={(record) => (recordHasTotalLabel(record) ? 'font-bold' : undefined)}
        headerStyle={{ padding: '5px', minHeight: 40, backgroundColor: '#f5f5f5' }}
      />
    </div>
  )
})
